import { useEffect, useCallback } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { COMMAND_PRIORITY_HIGH, PASTE_COMMAND } from "lexical";
import { $generateNodesFromDOM } from "@lexical/html";
import { $getSelection, $insertNodes } from "lexical";

import {
  getNormalizedTextLength,
  getNormalizedSelectionLength
} from "../utils/textContent";

interface PasteHandlerPluginProps {
  allowLinks?: boolean;
  maxLength?: number;
}

const ALLOWED_TAGS = [
  "B",
  "STRONG",
  "I",
  "EM",
  "U",
  "UL",
  "OL",
  "LI",
  "BR",
  "P"
];

/**
 * Sanitizes pasted content to allow only specific formatting.
 */
function sanitizeDOM(element: HTMLElement, allowLinks: boolean): void {
  const allowedTags = [...ALLOWED_TAGS];
  if (allowLinks) {
    allowedTags.push("A");
  }

  Array.from(element.childNodes).forEach((node) => {
    if (node.nodeType === 1) {
      const el = node as HTMLElement;

      if (!allowedTags.includes(el.tagName)) {
        const textContent = el.textContent || "";
        if (typeof document !== "undefined") {
          const textNode = document.createTextNode(textContent);
          el.replaceWith(textNode);
        }
      } else {
        sanitizeDOM(el, allowLinks);
      }
    }
  });
}

/**
 * Handles paste events, sanitizes content, and enforces maxLength.
 * Preserves: bold, italic, underline, lists, and optionally links.
 */
export default function PasteHandlerPlugin({
  allowLinks = false,
  maxLength
}: PasteHandlerPluginProps) {
  const [editor] = useLexicalComposerContext();

  const getAvailableSpace = useCallback(
    (pastedTextLength: number) => {
      if (!maxLength) return pastedTextLength;

      return editor.getEditorState().read(() => {
        const currentLength = getNormalizedTextLength();
        const selection = $getSelection();
        const selectedTextLength = selection
          ? getNormalizedSelectionLength(selection.getTextContent())
          : 0;

        if (currentLength >= maxLength) {
          return Math.max(0, selectedTextLength);
        }
        return maxLength - currentLength + selectedTextLength;
      });
    },
    [editor, maxLength]
  );

  const insertTextWithLimit = useCallback(
    (text: string) => {
      if (!maxLength) {
        const selection = $getSelection();
        if (selection) {
          selection.insertText(text);
        }
        return;
      }

      const availableSpace = getAvailableSpace(text.length);

      if (availableSpace <= 0) {
        return;
      }

      const selection = $getSelection();
      if (!selection) return;

      const textToInsert =
        text.length > availableSpace ? text.substring(0, availableSpace) : text;

      selection.insertText(textToInsert);
    },
    [getAvailableSpace, maxLength]
  );

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        const clipboardEvent = event as any;
        clipboardEvent.preventDefault();

        const clipboardData = clipboardEvent.clipboardData;
        if (!clipboardData) return false;

        const htmlContent = clipboardData.getData("text/html");

        if (htmlContent && typeof window !== "undefined" && window.DOMParser) {
          const parser = new window.DOMParser();
          const doc = parser.parseFromString(htmlContent, "text/html");

          sanitizeDOM(doc.body, allowLinks);

          editor.update(() => {
            const selection = $getSelection();
            if (!selection) return;

            const nodes = $generateNodesFromDOM(editor, doc);

            if (maxLength) {
              const pastedText = doc.body.textContent || "";
              const availableSpace = getAvailableSpace(pastedText.length);

              // Preserve formatting if content fits, else truncate as plain text
              if (pastedText.length <= availableSpace) {
                $insertNodes(nodes);
              } else if (availableSpace > 0) {
                const truncatedText = pastedText.substring(0, availableSpace);
                selection.insertText(truncatedText);
              }
            } else {
              $insertNodes(nodes);
            }
          });

          return true;
        }

        const plainText = clipboardData.getData("text/plain");
        if (plainText) {
          editor.update(() => {
            insertTextWithLimit(plainText);
          });
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor, allowLinks, maxLength, getAvailableSpace, insertTextWithLimit]);

  return null;
}
